import { CSSProperties, FC, MouseEvent } from "react"
import {
    fontSizeS,
    IconRadioButtonSelected,
    IconRadioButtonUnselected,
    colorAttentionBlue50,
    colorFunctionalGray70,
    colorMiscGray40,
    iconSizeL,
    spacingM,
    spacingS,
} from "@rhumbix/rmbx_design_system_web"

import { Radio, RadioGroup as RG } from "react-radio-group"

/**
 * Shared label styling for the design-system-styled form controls (RadioButtonWithLabel and
 * CheckboxWithLabel), so radios and checkboxes render with identical typography and spacing.
 */
export const CONTROL_LABEL_STYLE: CSSProperties = {
    color: colorFunctionalGray70,
    fontSize: fontSizeS,
    fontWeight: 500,
    display: "flex",
    flexDirection: "row",
    margin: 0,
    paddingBottom: spacingS,
    paddingLeft: 4,
    paddingRight: 4,
    cursor: "pointer",
}

type RadioButtonProps = Radio.RadioProps & {
    id: string
    labelStyle?: CSSProperties
    labelText: string
    styles?: CSSProperties
    /** When true, removes the icon's left spacing so it sits flush at the label's content start. */
    flushLeft?: boolean
}

export const RadioButtonWithLabel: FC<RadioButtonProps> = (props: RadioButtonProps) => {
    const { id, checked, disabled, flushLeft, labelText, labelStyle, onClick, styles, value } = props

    // Explicitly hanlde color because it's a complex style
    let color = styles?.color || colorAttentionBlue50
    if (disabled) {
        color = colorMiscGray40
    } else if (!checked) {
        color = colorFunctionalGray70
    }

    const radioStyles = {
        height: iconSizeL,
        width: iconSizeL,
        // flushLeft drops the left gap so the icon aligns with a CheckboxWithLabel box beside it.
        marginLeft: flushLeft ? 0 : spacingS,
        marginRight: spacingS,
        // Allow overriding default styles
        ...(styles ?? {}),
        color: color,
    }
    const RadioIconComponent = checked ? IconRadioButtonSelected : IconRadioButtonUnselected
    return (
        <label htmlFor={id} style={{ ...CONTROL_LABEL_STYLE, ...(labelStyle ?? {}) }}>
            <Radio
                value={value}
                id={id}
                checked={!!checked}
                onClick={onClick}
                onChange={() => onClick}
                disabled={disabled}
                style={{
                    // Make invisible/0 pixel, we don't want this
                    // to have any visuals, our visuals will come from
                    // the Icon and the span below
                    width: 0,
                    height: 0,
                    opacity: 0,
                    margin: 0,
                    paddingLeft: 0,
                    // Despite width: 0, this right padding still occupies ~8px and pushes the icon
                    // right, so flushLeft drops it (along with the icon's marginLeft) to line the
                    // radio up with an adjacent CheckboxWithLabel box.
                    paddingRight: flushLeft ? 0 : spacingS,
                    cursor: "pointer",
                }}
            />
            <RadioIconComponent style={radioStyles} />
            <span style={{ paddingRight: spacingS }}>{labelText}</span>
        </label>
    )
}

type RadioGroupOption = {
    label: string
    value: any
}

type RadioGroupProps = {
    disabled?: boolean
    handleChange: (value: string) => void
    horizontal?: boolean
    labelStyle?: CSSProperties
    name: string
    options: RadioGroupOption[]
    value: any
    style?: CSSProperties
    /** When true, radios sit flush at the label's content start (aligns with adjacent checkboxes). */
    flushLeft?: boolean
}

export const RadioGroup: FC<RadioGroupProps> = ({ disabled, flushLeft, handleChange, horizontal, name, options, labelStyle, style, value }) => {
    return (
        <RG
            id={`rg-${name}`}
            selectedValue={value}
            style={{
                display: "flex",
                flexDirection: horizontal ? "row" : "column",
                flexWrap: "wrap",
                paddingBottom: spacingM,
                paddingLeft: 10,
                paddingRight: 0,
                paddingTop: 14,
                ...(style || {}),
            }}
        >
            {options.map((option: RadioGroupOption) => {
                return (
                    <RadioButtonWithLabel
                        id={`rg-${name}-option-${option.value}`}
                        key={`rg-${name}-option-${option.value}`}
                        value={option.value}
                        labelText={option.label}
                        disabled={disabled}
                        checked={value === option.value}
                        onClick={(e: MouseEvent<HTMLInputElement>) => {
                            const input = e.target as HTMLInputElement
                            handleChange(input.value)
                        }}
                        labelStyle={labelStyle}
                        flushLeft={flushLeft}
                    />
                )
            })}
        </RG>
    )
}
